import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as campaignContactActions from '@redux/modules/campaignContact'

import defaultFormState, { requiredFields } from '@models/campaignContact'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type { CampaignContactModel, CampaignContactRequestOptions, ContactModel } from '@models/types'

export type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (
  campaignContact: Partial<CampaignContactModel> = {},
) => {
  const { campaign_id } = campaignContact

  const campaignContactsModalPayload = encodeURIComponent(
    JSON.stringify({ campaignContact: { campaign_id } }),
  )

  return {
    addCampaignContactUrl:
    `#/campaigns/${campaign_id}/edit/contacts?showModal=CreateCampaignContactModal&showModalPayload=
    ${campaignContactsModalPayload}`,
  }
}

type CreateCampaignContactParams = {
  campaignContactParams: Partial<CampaignContactModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignContactRequestOptions,
}

const createCampaignContact = (params: CreateCampaignContactParams) => {
  const { dispatch, campaignContactParams, requestOptions } = params
  const { createCampaignContact: createFn } = campaignContactActions

  return dispatch(createFn(campaignContactParams, requestOptions))
}

type CreateWithContactParams = {
  campaignContact: Partial<CampaignContactModel>,
  contactParams: Partial<ContactModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignContactRequestOptions,
}

const createWithContact = (params: CreateWithContactParams) => {
  const {
    dispatch, campaignContact, contactParams, requestOptions,
  } = params

  const { createWithContact: createFn } = campaignContactActions

  return dispatch(createFn(campaignContact, contactParams, requestOptions))
}

type DeleteCampaignContactParams = {
  campaignContact: DeleteParams<CampaignContactModel>,
  dispatch: AppDispatch,
}

const deleteCampaignContact = (params: DeleteCampaignContactParams) => {
  const { dispatch, campaignContact } = params
  const { deleteCampaignContact: deleteFn } = campaignContactActions

  return dispatch(deleteFn(campaignContact))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignContactForm(
  campaignContact: Partial<CampaignContactModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignContactForm = useForm(
    defaultFormState,
    { entity: campaignContact, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignContact.id, campaignContact.cache_key],
  )

  return {
    ...campaignContactForm,
  }
}

export const useRelations = (campaignContact: Partial<CampaignContactModel> = {}) => {
  const { campaign_id, contact_id } = campaignContact

  const { campaigns, contacts } = useSelector(reduxState => reduxState.entities)

  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}
  const contact = contact_id && contacts[contact_id] ? contacts[contact_id] : {}

  return {
    campaign,
    contact,
  }
}

function useCampaignContact(initEntity: Partial<CampaignContactModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: campaignContact }: { entity: CampaignContactModel } = useLatestEntity(initEntity, 'campaignContacts')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { campaign, contact } = useRelations(campaignContact)

  const {
    // @ts-expect-error TS2339
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignContacts)

  return {
    callbacks: {
      createCampaignContact: (
        campaignContactParams: Partial<CampaignContactModel>,
        entityOptions?: CampaignContactRequestOptions,
      ) => (
        createCampaignContact({ campaignContactParams, dispatch, requestOptions: entityOptions })
      ),
      createWithContact: (
        contactParams: Partial<ContactModel>,
        entityOptions?: CampaignContactRequestOptions,
      ) => (
        createWithContact({
          campaignContact, contactParams, dispatch, requestOptions: entityOptions,
        })
      ),
      createCampaignContactModal: () => launchModal({
        callbacks,
        modalKey: 'CreateCampaignContactModal',
        payload: { campaignContact },
      }),
      deleteCampaignContact: () => deleteCampaignContact({ dispatch, campaignContact }),
    },
    campaign,
    campaignContact,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(campaignContact.id),
    contact,
    updating,
    urls: generateUrls(campaignContact),
  }
}

export type UseCampaignContactPayload = ReturnType<typeof useCampaignContact>

export default useCampaignContact
